import { AttachMoney, Download, Email, Phone, Refresh, Search } from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { RefreshData } from "components/helper";
import Loader from "components/Loader";
import { useEffect, useMemo, useState } from "react";
import { FaCreditCard, FaMoneyBillWave, FaReceipt } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const ReferralClients = ({ myReferrals = false }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  // Mock data - replace with actual API call

  const { id: idFromUrl } = useParams();
  const { state } = useLocation();

  const getUserName = useMemo(() => {
    const userNameFromToken = JSON.parse(localStorage.getItem("userDetail"))?.name;
    if (state) {
      return state?.user?.name;
    } else return userNameFromToken;
  }, [idFromUrl]);

  const id = useMemo(() => {
    const idFromToken = JSON.parse(localStorage.getItem("userDetail"))?.id;
    if (idFromUrl) {
      return idFromUrl;
    } else return idFromToken;
  }, [idFromUrl]);

  const getReferralClients = async (id) => {
    const url = myReferrals ? `referrals/me` : `referrals/${id}/clients`;

    const res = await axios.get(url);
    return myReferrals ? res?.data?.data?.entries : res?.data?.data;
  };

  const {
    refetch: refetchReferralClients,
    data: referralClientsList,
    isFetching: isReferralClientsListFetching,
  } = useQuery({
    queryKey: ["referralClients", id],
    queryFn: () => getReferralClients(id),
    enabled: false,
  });

  useEffect(() => {
    if (id && !referralClientsList) {
      refetchReferralClients(id);
    }
  }, [id]);

  const filteredClients =
    referralClientsList?.filter(
      (client) =>
        client?.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client?.referralName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client?.clientEmail?.toLowerCase().includes(searchTerm.toLowerCase())
    ) ?? [];
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount);
  };
  const getOverallPending = () => {
    return filteredClients?.reduce((acc, client) => acc + client.totalDue, 0);
  };

  const getOverallPaid = () => {
    return filteredClients?.reduce((acc, client) => acc + client.totalPaid, 0);
  };

  const getOverallReferralAmount = () => {
    return filteredClients?.reduce((acc, client) => acc + client.totalReferral, 0);
  };

  return (
    <div className="w-full mb-12 px-4 dashBody mt-4">
      {isReferralClientsListFetching && <Loader />}

      {/* Overall Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-2">
        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Referrals</p>
              <p className="text-3xl font-bold text-app-text">{filteredClients?.length}</p>
            </div>
            <FaMoneyBillWave className="text-4xl text-blue-500" />
          </div>
        </div>
        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Referral Amount</p>
              <p className="text-3xl font-bold text-app-text">
                {formatCurrency(getOverallReferralAmount())}
              </p>
            </div>
            <FaReceipt className="text-4xl text-yellow-500" />
          </div>
        </div>{" "}
        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Paid</p>
              <p className="text-3xl font-bold text-app-text">{formatCurrency(getOverallPaid())}</p>
            </div>
            <FaCreditCard className="text-4xl text-green-500" />
          </div>
        </div>
        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Pending</p>
              <p className="text-3xl font-bold text-app-text">
                {formatCurrency(getOverallPending())}
              </p>
            </div>
            <FaReceipt className="text-4xl text-yellow-500" />
          </div>
        </div>
      </div>
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl border border-app-border h-full">
        {/* Header */}
        <div className="flex-shrink-0 rounded-t-xl mb-0 px-8 py-4 border-b border-app-border bg-app-surface">
          <div className="flex flex-wrap items-center justify-between">
            <div className="relative flex justify-start gap-4 items-center">
              <h3 className="flex items-center gap-2 font-semibold text-lg text-app-text">
                <IoArrowBack className="cursor-pointer" onClick={() => navigate(-1)} />
                {getUserName ? `Referral Clients of ${getUserName}` : "Referral Clients"}
              </h3>
              <Badge badgeContent={referralClientsList?.length} color="primary" />
              <RefreshData refreshData={() => refetchReferralClients(id)} />
            </div>

            <div className="flex items-center gap-2">
              {/* <Button
                variant="outlined"
                startIcon={<Download />}
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                Export
              </Button> */}
            </div>
          </div>
          <p className="mt-2 text-sm text-app-text-secondary">
            Referrals recorded before the referral portal. This list is read-only; new referral
            amounts are entered on the client&apos;s account.
          </p>
        </div>

        {/* Filters and Search */}
        <div className="flex-shrink-0 px-8 py-4 border-b border-app-border bg-app-surface-muted">
          <div className="flex flex-wrap items-center gap-4">
            <TextField
              size="small"
              placeholder="Search clients, referrals"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="text-app-text-muted" />
                  </InputAdornment>
                ),
              }}
              className="min-w-[300px]"
            />

            <TextField
              select
              size="small"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
              className="min-w-[150px]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="inactive">Inactive</option>
            </TextField>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              className="border-app-border text-app-text-secondary hover:bg-app-surface-hover"
              onClick={() => refetchReferralClients(id)}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <TableContainer component={Paper} className="shadow-none border-0">
            <Table className="min-w-full">
              <TableHead className="bg-app-surface-muted">
                <TableRow>
                  <TableCell className="font-semibold text-app-text">Client</TableCell>

                  <TableCell className="font-semibold text-app-text">Total Referral</TableCell>
                  <TableCell className="font-semibold text-app-text">Total Paid</TableCell>
                  <TableCell className="font-semibold text-app-text">Total Due</TableCell>

                  {/* <TableCell className="font-semibold text-app-text">
                    Actions
                  </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients?.map((client) => (
                  <TableRow
                    key={client?.id}
                    className="hover:bg-app-surface-hover cursor-pointer"
                    onClick={() => {
                      navigate(`/admin/admin-referral/${id}/clients/${client.clientId}`, {
                        state: {
                          referrerUserId: state?.referrerUserId,
                          client: client,
                        },
                      });
                    }}
                  >
                    {/* Client Information */}
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="w-10 h-10 bg-blue-100 text-primary">
                          {client.clientName.charAt(0)}
                        </Avatar>
                        <div>
                          <div className="font-semibold text-app-text">{client.clientName}</div>
                          <div className="text-sm text-app-text-secondary flex items-center gap-2">
                            <Email className="w-3 h-3" />
                            {client.email}
                          </div>
                          <div className="text-sm text-app-text-secondary flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            {client.phone}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    {/* Referral Information */}

                    {/* Amount */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <AttachMoney className="text-green-600" />
                        <span className="font-semibold text-green-600">
                          {client?.totalReferral?.toLocaleString()}
                        </span>
                      </div>
                    </TableCell>

                    {/* Total Paid */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <AttachMoney className="text-primary" />
                        <span className="font-semibold text-primary">
                          {client?.totalPaid?.toLocaleString()}
                        </span>
                      </div>
                    </TableCell>

                    {/* Total Due */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <AttachMoney className="text-primary" />
                        <span className="font-semibold text-primary">
                          {client?.totalDue?.toLocaleString()}
                        </span>
                      </div>
                    </TableCell>

                    {/* Actions */}
                    {/* <TableCell>
                      <div className="flex items-center gap-2">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            className="text-primary hover:bg-blue-50"
                            onClick={() =>
                              navigate(
                                `/admin/referral/view/${client.clientId}`
                              )
                            }
                          >
                            <Visibility className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() =>
                              navigate(`/admin/referral/edit/${client.id}`)
                            }
                          >
                            <Edit className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Delete className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {filteredClients?.length === 0 && (
            <div className="text-center py-12">
              <div className="text-app-text-muted mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-app-text mb-2">No clients found</h3>
              <p className="text-app-text-secondary mb-4">
                {searchTerm
                  ? `No results found for "${searchTerm}"`
                  : "No referral clients available"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralClients;
